import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';

export interface ApiError {
  message: string;
  errorCode?: string;
  statusCode: number;
  /** 422 answer validation: messages keyed `{submissionIndex}.{fieldKey}` (events dynamic forms). */
  fieldErrors?: Record<string, string>;
}

const ERROR_CODE_MAP: Record<string, string> = {
  // Auth
  INVALID_CREDENTIALS:          'errors.invalidCredentials',
  ACCOUNT_LOCKED:               'errors.accountLocked',
  ACCOUNT_DEACTIVATED:          'errors.accountDeactivated',
  INVALID_REFRESH_TOKEN:        'errors.invalidRefreshToken',
  INVALID_VERIFICATION_CODE:    'errors.invalidVerificationCode',
  USER_NOT_FOUND:               'errors.userNotFound',
  USER_ALREADY_EXISTS:          'errors.userAlreadyExists',
  EMAIL_ALREADY_REGISTERED:     'errors.emailAlreadyRegistered',
  UNSUPPORTED_OAUTH_PROVIDER:   'errors.unsupportedOAuthProvider',
  OAUTH_ACCOUNT_NOT_FOUND:      'errors.oauthAccountNotFound',
  EMAIL_MISMATCH:               'errors.emailMismatch',
  EXTERNAL_LOGIN_ALREADY_LINKED:'errors.externalLoginAlreadyLinked',
  PASSWORD_ALREADY_SET:         'errors.passwordAlreadySet',
  INVALID_RESET_CODE:           'errors.invalidResetCode',
  RESET_CODE_EXPIRED:           'errors.resetCodeExpired',

  // Company lookup ("invoice my company" checkout)
  COMPANY_NOT_FOUND:                  'billing.companyLookupNotFound',
  COMPANY_LOOKUP_UNAVAILABLE:         'billing.companyLookupUnavailable',
  COMPANY_VAT_CODE_INVALID:           'billing.companyVatCodeInvalid',
  COMPANY_LOOKUP_COUNTRY_UNSUPPORTED: 'billing.companyLookupCountryUnsupported',

  // Booking
  COURT_NOT_FOUND_OR_INACTIVE:  'errors.courtNotFoundOrInactive',
  MIN_BOOKING_DURATION:         'errors.minBookingDuration',
  MAX_BOOKING_DURATION:         'errors.maxBookingDuration',
  COMPLEX_CLOSED_ON_DAY:        'errors.complexClosedOnDay',
  BOOKING_OUTSIDE_WORKING_HOURS:'errors.bookingOutsideWorkingHours',
  BOOKING_CONFLICT:             'errors.bookingConflict',
  TIME_SLOT_BLOCKED:            'errors.timeSlotBlocked',
  PAYMENT_REQUIRED:             'errors.paymentRequired',
  PAYMENT_NOT_COMPLETED:        'errors.paymentNotCompleted',
  PAYMENT_ALREADY_USED:         'errors.paymentAlreadyUsed',
  SPORT_PROMOTION_NO_LONGER_AVAILABLE: 'errors.sportPromotionNoLongerAvailable',
  BOOKING_NOT_FOUND:            'errors.bookingNotFound',
  BOOKING_ALREADY_CANCELLED:    'errors.bookingAlreadyCancelled',
  BOOKING_ALREADY_STARTED:      'errors.bookingAlreadyStarted',
  BOOKING_CONCURRENCY_CONFLICT: 'errors.bookingConcurrencyConflict',
  MULTI_SLOT_NOT_ALLOWED:       'errors.multiSlotNotAllowed',
  VALIDATION_FAILED:            'errors.validationFailed',

  // Complex / Court
  COMPLEX_NOT_FOUND:            'errors.complexNotFound',
  COURT_NOT_FOUND:              'errors.courtNotFound',
  SPORT_TYPE_NOT_FOUND:         'errors.sportTypeNotFound',
  WORKING_HOURS_NOT_CONFIGURED: 'errors.workingHoursNotConfigured',
  COMPLEX_SUSPENDED_BY_ADMIN:   'errors.complexSuspended',
  PAYMENT_NOT_ENABLED:          'errors.paymentNotEnabled',
  NO_PRICE_CONFIGURED:          'errors.noPriceConfigured',

  // Generic HTTP
  NETWORK_ERROR:                'errors.networkError',
};

const HTTP_STATUS_MAP: Record<number, string> = {
  0:   'errors.networkError',
  401: 'errors.unauthorized',
  403: 'errors.forbidden',
  404: 'errors.notFound',
  408: 'errors.requestTimeout',
  429: 'errors.tooManyRequests',
  500: 'errors.serverError',
  502: 'errors.serverError',
  503: 'errors.serverUnavailable',
};

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const body = error.error;
      const errorCode: string | undefined = body?.errorCode ?? body?.code;

      // Store the i18n key as the message — InlineErrorComponent translates it at render time
      let message: string;
      if (errorCode && ERROR_CODE_MAP[errorCode]) {
        message = ERROR_CODE_MAP[errorCode];
      } else if (HTTP_STATUS_MAP[error.status]) {
        message = HTTP_STATUS_MAP[error.status];
      } else {
        message = body?.error ?? body?.message ?? body?.title ?? 'errors.unexpected';
      }

      const apiError: ApiError = {
        message,
        errorCode,
        statusCode: error.status,
        fieldErrors: body?.fieldErrors,
      };
      return throwError(() => apiError);
    }),
  );
};
